import React, { useState, useEffect } from 'react';
import { Form, Input, InputNumber, DatePicker, Select, Button, Card, message, Space, Descriptions, Tag, Spin, Popconfirm } from 'antd';
import { useQuery, useMutation } from '@apollo/client';
import { useNavigate, useParams } from 'react-router-dom';
import { getCreditProductById } from '../../graphql/queries';
import { updateCreditProduct, upsertProductOffering, deleteProductOffering, deleteCreditProduct } from '../../graphql/mutation';
import moment from 'moment';
import './CreditProducts.css';

const { Option } = Select;

const CreditProductDetail = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { id } = useParams();
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [offeringRows, setOfferingRows] = useState([]);
  const [newOffering, setNewOffering] = useState({ presentedOfferingId: '', amount: null });
  const [savingOfferingId, setSavingOfferingId] = useState(null);

  const { loading: queryLoading, data, refetch } = useQuery(getCreditProductById, {
    variables: { getCreditProductByIdId: id },
    onError: (error) => {
      message.error(`Error loading credit product: ${error.message}`);
    },
  });

  const [updateProduct] = useMutation(updateCreditProduct, {
    onCompleted: (data) => {
      message.success('Credit product updated successfully!');
      setIsEditing(false);
      refetch();
    },
    onError: (error) => {
      message.error(`Error updating credit product: ${error.message}`);
      setLoading(false);
    },
  });

  const [upsertOffering] = useMutation(upsertProductOffering);
  const [removeOffering] = useMutation(deleteProductOffering);
  const [removeProduct] = useMutation(deleteCreditProduct);

  useEffect(() => {
    if (data?.getCreditProductById) {
      const product = data.getCreditProductById;
      form.setFieldsValue({
        name: product.name,
        amount: product.amount,
        startDate: moment(),
        endDate: moment().add(5, 'years'),
        price: product.price,
        type: product.type,
      });
      setOfferingRows(
        (product.offerings || []).map((o) => ({
          id: o.id,
          presentedOfferingId: o.presentedOfferingId,
          originalPresentedOfferingId: o.presentedOfferingId,
          amount: o.amount,
        })),
      );
    }
  }, [data, form]);

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const input = {
        name: values.name,
        amount: values.amount,
        startDate: moment(),
        endDate: moment().add(5, 'years'),
        price: values.price,
        type: values.type,
      };

      await updateProduct({
        variables: { 
          input,
          updateCreditProductId: id
        },
      });
    } catch (error) {
      console.error('Error updating credit product:', error);
    }
  };

  const handleOfferingRowChange = (rowId, field, value) => {
    setOfferingRows((rows) =>
      rows.map((row) => (row.id === rowId ? { ...row, [field]: value } : row)),
    );
  };

  const handleSaveOfferingRow = async (row) => {
    const nextOfferingId = (row.presentedOfferingId || '').trim();
    if (!nextOfferingId) {
      message.error('Offering ID cannot be empty');
      return;
    }
    if (row.amount === null || row.amount === undefined) {
      message.error('Amount cannot be empty');
      return;
    }

    setSavingOfferingId(row.id);
    try {
      const isRename = nextOfferingId !== row.originalPresentedOfferingId;
      if (isRename) {
        // upsert is keyed on (productId, presentedOfferingId), so renaming
        // must drop the old key first or it creates a second row instead
        // of updating this one.
        await removeOffering({ variables: { id: row.id } });
      }
      await upsertOffering({
        variables: {
          productId: id,
          presentedOfferingId: nextOfferingId,
          amount: row.amount,
        },
      });
      message.success('Offering saved!');
      refetch();
    } catch (error) {
      message.error(`Error saving offering: ${error.message}`);
    } finally {
      setSavingOfferingId(null);
    }
  };

  const handleDeleteOffering = async (offeringId) => {
    try {
      await removeOffering({ variables: { id: offeringId } });
      message.success('Offering removed!');
      refetch();
    } catch (error) {
      message.error(`Error removing offering: ${error.message}`);
    }
  };

  const handleAddOffering = async () => {
    if (!newOffering.presentedOfferingId.trim()) {
      message.error('Offering ID cannot be empty');
      return;
    }
    if (newOffering.amount === null || newOffering.amount === undefined) {
      message.error('Amount cannot be empty');
      return;
    }
    setSavingOfferingId('new');
    try {
      await upsertOffering({
        variables: {
          productId: id,
          presentedOfferingId: newOffering.presentedOfferingId.trim(),
          amount: newOffering.amount,
        },
      });
      message.success('Offering saved!');
      setNewOffering({ presentedOfferingId: '', amount: null });
      refetch();
    } catch (error) {
      message.error(`Error saving offering: ${error.message}`);
    } finally {
      setSavingOfferingId(null);
    }
  };

  const handleDeleteProduct = async () => {
    try {
      await removeProduct({ variables: { deleteCreditProductId: id } });
      message.success('Credit product deleted successfully!');
      navigate('/credit-products');
    } catch (error) {
      message.error(`Error deleting credit product: ${error.message}`);
    }
  };

  const productTypes = [
    { value: 'YEARLY', label: 'Yearly' },
    { value: 'WEEKLY', label: 'Weekly' },
    { value: 'MONTHLY', label: 'Monthly' },
    { value: 'LIFETIME', label: 'Lifetime' },
    { value: 'PROMO', label: 'Promo' },
  ];

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const getStatusTag = (product) => {
    if (product.isExpired) {
      return <Tag color="red">Expired</Tag>;
    }
    if (product.isUpcoming) {
      return <Tag color="blue">Upcoming</Tag>;
    }
    if (product.isActive) {
      return <Tag color="green">Active</Tag>;
    }
    return <Tag color="default">Inactive</Tag>;
  };

  if (queryLoading) {
    return (
      <div className="credit-products-container">
        <Card>
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Spin size="large" />
          </div>
        </Card>
      </div>
    );
  }

  const product = data?.getCreditProductById;

  return (
    <div className="credit-products-container">
      <Card>
        <div className="table-header">
          <h2>Credit Product Details</h2>
          <Space>
            {!isEditing && (
              <Button type="primary" onClick={() => setIsEditing(true)}>
                Edit Product
              </Button>
            )}
            <Popconfirm
              title="Delete this credit product?"
              description="Products with associated credits cannot be deleted."
              onConfirm={handleDeleteProduct}
              okText="Yes"
              cancelText="No"
            >
              <Button danger>Delete Product</Button>
            </Popconfirm>
            <Button onClick={() => navigate('/credit-products')}>
              Back to List
            </Button>
          </Space>
        </div>

        {isEditing ? (
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
          >
            <Form.Item
              name="name"
              label="Product Name"
              rules={[{ required: true, message: 'Please enter a product name' }]}
            >
              <Input placeholder="Enter product name" />
            </Form.Item>

            <Form.Item
              name="type"
              label="Product Type"
              rules={[{ required: true, message: 'Please select a product type' }]}
            >
              <Select placeholder="Select product type">
                {productTypes.map((type) => (
                  <Option key={type.value} value={type.value}>
                    {type.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              name="amount"
              label="Credit Amount"
              rules={[{ required: true, message: 'Please enter credit amount' }]}
            >
              <InputNumber
                min={0}
                style={{ width: '100%' }}
                placeholder="Enter credit amount"
              />
            </Form.Item>

            <div style={{ borderLeft: '2px solid #f0f0f0', paddingLeft: 16, marginBottom: 24, marginTop: -12 }}>
              <div style={{ color: 'rgba(0, 0, 0, 0.45)', marginBottom: 8 }}>
                Offering overrides — used instead of the amount above when RevenueCat reports that presented offering.
              </div>

              {offeringRows.map((row) => (
                <Space key={row.id} style={{ marginBottom: 8 }}>
                  <Input
                    value={row.presentedOfferingId}
                    onChange={(e) => handleOfferingRowChange(row.id, 'presentedOfferingId', e.target.value)}
                    placeholder="Offering ID"
                    style={{ width: 200 }}
                  />
                  <InputNumber
                    min={0}
                    value={row.amount}
                    onChange={(value) => handleOfferingRowChange(row.id, 'amount', value)}
                    placeholder="Amount"
                    style={{ width: 120 }}
                  />
                  <Button
                    type="primary"
                    loading={savingOfferingId === row.id}
                    onClick={() => handleSaveOfferingRow(row)}
                  >
                    Save
                  </Button>
                  <Popconfirm
                    title="Remove this offering override?"
                    onConfirm={() => handleDeleteOffering(row.id)}
                    okText="Yes"
                    cancelText="No"
                  >
                    <Button danger>Delete</Button>
                  </Popconfirm>
                </Space>
              ))}

              <Space>
                <Input
                  value={newOffering.presentedOfferingId}
                  onChange={(e) => setNewOffering((o) => ({ ...o, presentedOfferingId: e.target.value }))}
                  placeholder="Offering ID"
                  style={{ width: 200 }}
                />
                <InputNumber
                  min={0}
                  value={newOffering.amount}
                  onChange={(value) => setNewOffering((o) => ({ ...o, amount: value }))}
                  placeholder="Amount"
                  style={{ width: 120 }}
                />
                <Button loading={savingOfferingId === 'new'} onClick={handleAddOffering}>
                  Add
                </Button>
              </Space>
            </div>

            <Form.Item
              name="price"
              label="Price (USD)"
              rules={[{ required: true, message: 'Please enter price' }]}
            >
              <InputNumber
                min={0}
                step={0.01}
                style={{ width: '100%' }}
                placeholder="Enter price in USD"
              />
            </Form.Item>

            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit" loading={loading}>
                  Save Changes
                </Button>
                <Button onClick={() => {
                  setIsEditing(false);
                  form.resetFields();
                }}>
                  Cancel
                </Button>
              </Space>
            </Form.Item>
          </Form>
        ) : (
          <Descriptions bordered column={2}>
            <Descriptions.Item label="Product Name">{product.name}</Descriptions.Item>
            <Descriptions.Item label="Product Type">
              <Tag color={product.type === 'PREMIUM' ? 'gold' : 'blue'}>
                {product.type}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Credit Amount">{product.amount}</Descriptions.Item>
            {product.offerings && product.offerings.length > 0 && (
              <Descriptions.Item label="Offering Overrides" span={2}>
                <Space wrap>
                  {product.offerings.map((offering) => (
                    <Tag key={offering.id}>
                      {offering.presentedOfferingId}: {offering.amount}
                    </Tag>
                  ))}
                </Space>
              </Descriptions.Item>
            )}
            <Descriptions.Item label="Price">{formatCurrency(product.price)}</Descriptions.Item>
            <Descriptions.Item label="Credit Count">{product.creditCount}</Descriptions.Item>
            <Descriptions.Item label="Start Date">{formatDate(product.startDate)}</Descriptions.Item>
            <Descriptions.Item label="End Date">{formatDate(product.endDate)}</Descriptions.Item>
            <Descriptions.Item label="Status">{getStatusTag(product)}</Descriptions.Item>
            <Descriptions.Item label="Created At">{formatDate(product.createdAt)}</Descriptions.Item>
            <Descriptions.Item label="Updated At">{formatDate(product.updatedAt)}</Descriptions.Item>
          </Descriptions>
        )}
      </Card>
    </div>
  );
};

export default CreditProductDetail; 